import { Component, HostBinding, OnInit } from '@angular/core';
import { User } from '../../core/models/user.model';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { AuthService } from '../../core/services/auth.service';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NavigationEnd, Router } from '@angular/router';
import { FEATURE_KEYS } from '../../core/models/feature-flag.model';
import { FeatureFlagService } from '../../core/services/feature-flag.service';
import { ReportingService } from '../../core/services/reporting.service';
import { filter } from 'rxjs/operators';
import { UTILITY_REGISTRY } from '../../features/utilities/utility-registry';

interface DashboardNavItem {
  path?: string;
  label: string;
  icon: string;
  children?: DashboardNavItem[];
  featureKey?: string;
  requiresReports?: boolean;
}

const utilityNavItems: DashboardNavItem[] = [
  { path: '/dashboard/utilities', label: 'Overview', icon: 'space_dashboard' },
  ...UTILITY_REGISTRY.map(item => ({ path: item.route, label: item.title, icon: item.icon || 'build' }))
];

@Component({
  selector: 'app-dashboard-nav',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './dashboard-nav.component.html',
  styleUrls: ['./dashboard-nav.component.css']
})
export class DashboardNavComponent implements OnInit {
  readonly companyLogo = 'assets/branding/logo.png';
  private readonly collapsedStorageKey = 'riyashuma.sidebar.collapsed';
  currentUser: User | null = null;
  isPlatformEmployee = false;
  isOperator = false;
  isAdmin = false;
  isCollapsed = false;
  featureStatesLoaded = false;
  effectiveStates: Record<string, boolean> = {};
  reportsAvailable = false;
  expandedGroups = new Set<string>();

  @HostBinding('class.collapsed')
  get collapsedClass(): boolean {
    return this.isCollapsed;
  }
  
  platformEmployeeNavItems: DashboardNavItem[] = [
    { path: '/dashboard', label: 'Overview', icon: 'dashboard' },
    { path: '/dashboard/live-map', label: 'Fleet Monitor', icon: 'map' },
    { path: '/dashboard/reports', label: 'Reports', icon: 'assessment', requiresReports: true },
    { path: '/dashboard/system-logs', label: 'System Logs', icon: 'manage_search' },
    { path: '/dashboard/organisations', label: 'Operators', icon: 'business' },
    { path: '/dashboard/onboarding', label: 'Onboarding', icon: 'assignment_turned_in' },
    { path: '/dashboard/vehicles', label: 'Vehicles', icon: 'directions_car' },
    { path: '/dashboard/routes', label: 'Routes', icon: 'route' },
    { path: '/dashboard/drivers', label: 'Drivers', icon: 'person' },
    { path: '/dashboard/bookings', label: 'Bookings', icon: 'book_online', featureKey: FEATURE_KEYS.operatorBookingManagement },
    { path: '/dashboard/passengers/profiles', label: 'Passenger Profiles', icon: 'groups', featureKey: FEATURE_KEYS.passengerProfiles },
    { path: '/dashboard/devices', label: 'Devices', icon: 'devices' },
    { path: '/dashboard/device-sims', label: 'SIMs', icon: 'sim_card' },
    {
      label: 'Utilities',
      icon: 'construction',
      children: utilityNavItems
    }
  ];

  adminNavItems: DashboardNavItem[] = [
    { path: '/dashboard', label: 'Overview', icon: 'dashboard' },
    { path: '/dashboard/live-map', label: 'Fleet Monitor', icon: 'map' },
    { path: '/dashboard/reports', label: 'Reports', icon: 'assessment', requiresReports: true },
    { path: '/dashboard/system-logs', label: 'System Logs', icon: 'manage_search' },
    { path: '/dashboard/organisations', label: 'Operators', icon: 'business' },
    { path: '/dashboard/onboarding', label: 'Onboarding', icon: 'assignment_turned_in' },
    { path: '/dashboard/vehicles', label: 'Vehicles', icon: 'directions_car' },
    { path: '/dashboard/routes', label: 'Routes', icon: 'route' },
    { path: '/dashboard/trips', label: 'Trips', icon: 'schedule' },
    { path: '/dashboard/drivers', label: 'Drivers', icon: 'person' },
    { path: '/dashboard/bookings', label: 'Bookings', icon: 'book_online', featureKey: FEATURE_KEYS.operatorBookingManagement },
    { path: '/dashboard/passengers/profiles', label: 'Passenger Profiles', icon: 'groups', featureKey: FEATURE_KEYS.passengerProfiles },
    {
      label: 'Hotspot',
      icon: 'wifi',
      children: [
        { path: '/dashboard/hotspot/passengers', label: 'Hotspot Sync', icon: 'sync_alt' },
        { path: '/dashboard/hotspot/scheduled-sync', label: 'Scheduled Sync', icon: 'sync' },
        { path: '/dashboard/hotspot/session-records', label: 'Session Records', icon: 'travel_explore' },
        { path: '/dashboard/hotspot/asset-mappings', label: 'Asset Mappings', icon: 'hub' }
      ]
    },
    { path: '/dashboard/users', label: 'Users', icon: 'people' },
    { path: '/dashboard/devices', label: 'Devices', icon: 'devices' },
    { path: '/dashboard/device-sims', label: 'SIMs', icon: 'sim_card' },
    { path: '/dashboard/features', label: 'Feature management', icon: 'toggle_on' },
    { path: '/dashboard/profile', label: 'Profile', icon: 'person' },
    {
      label: 'Utilities',
      icon: 'construction',
      children: utilityNavItems
    }
  ];

  operatorNavItems: DashboardNavItem[] = [
    { path: '/dashboard', label: 'Overview', icon: 'dashboard' },
    { path: '/dashboard/live-map', label: 'Live Map', icon: 'map' },
    { path: '/dashboard/trips', label: 'Schedule Trips', icon: 'schedule' },
    { path: '/dashboard/bookings', label: 'Bookings', icon: 'book_online', featureKey: FEATURE_KEYS.operatorBookingManagement },
    { path: '/dashboard/vehicles', label: 'My Vehicles', icon: 'directions_car' },
    { path: '/dashboard/routes', label: 'Routes', icon: 'route' },
    { path: '/dashboard/drivers', label: 'Drivers', icon: 'person' },
    { path: '/dashboard/passengers/profiles', label: 'Passenger Profiles', icon: 'groups', featureKey: FEATURE_KEYS.passengerProfiles },
    { path: '/dashboard/reports', label: 'Reports', icon: 'assessment', requiresReports: true },
    { path: '/dashboard/profile', label: 'Organisation Profile', icon: 'business' }
  ];

  // Passenger navigation items
  passengerNavItems: DashboardNavItem[] = [
    { path: '/dashboard', label: 'Overview', icon: 'dashboard' },
    { path: '/dashboard/search', label: 'Find Trips', icon: 'search' },
    { path: '/dashboard/bookings', label: 'My Bookings', icon: 'book_online' },
    { path: '/dashboard/live-map', label: 'Trip Map', icon: 'map' },
    { path: '/dashboard/reports', label: 'Reports', icon: 'assessment', requiresReports: true },
    { path: '/dashboard/profile', label: 'My Profile', icon: 'person' }
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private featureFlags: FeatureFlagService,
    private reporting: ReportingService
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    this.isCollapsed = localStorage.getItem(this.collapsedStorageKey) === 'true';
    const role = this.getCurrentRole();

    this.isAdmin = role === UserRole.ADMIN;
    this.isPlatformEmployee = role === UserRole.PLATFORM_EMPLOYEE;
    this.isOperator = role === UserRole.OPERATOR;

    this.featureFlags.effectiveLoadState$.subscribe(state => this.featureStatesLoaded = state === 'loaded');
    this.featureFlags.effectiveFeatures$.subscribe(states => this.effectiveStates = states);
    this.featureFlags.loadEffectiveFeatures().subscribe({ error: () => undefined });
    this.reporting.getCatalogue().subscribe({
      next: catalogue => this.reportsAvailable = catalogue.length > 0,
      error: () => this.reportsAvailable = false
    });
    this.expandActiveGroup();
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => this.expandActiveGroup());
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
    localStorage.setItem(this.collapsedStorageKey, String(this.isCollapsed));
  }

  getNavItems(): DashboardNavItem[] {
    const items = this.isAdmin
      ? this.adminNavItems
      : this.isPlatformEmployee
        ? this.platformEmployeeNavItems
        : this.isOperator
          ? this.operatorNavItems
          : this.passengerNavItems;
    return items.filter(item => this.isItemVisible(item));
  }

  private isItemVisible(item: DashboardNavItem): boolean {
    if (item.requiresReports && !this.reportsAvailable) return false;
    if (!item.featureKey) return true;
    return this.featureStatesLoaded && this.effectiveStates[item.featureKey] === true;
  }

  isNavGroupActive(item: DashboardNavItem): boolean {
    return Boolean(item.children?.some(child => child.path && this.router.isActive(child.path, false)));
  }

  isGroupExpanded(item: DashboardNavItem): boolean {
    return this.expandedGroups.has(item.label) || this.isNavGroupActive(item);
  }

  toggleGroup(item: DashboardNavItem): void {
    if (this.expandedGroups.has(item.label)) this.expandedGroups.delete(item.label);
    else this.expandedGroups.add(item.label);
  }

  private expandActiveGroup(): void {
    this.getNavItems().filter(item => this.isNavGroupActive(item)).forEach(item => this.expandedGroups.add(item.label));
  }

  getDisplayName(): string {
    return this.currentUser?.userName || this.currentUser?.username || 'User';
  }

  getBrandLogo(): string {
    if (this.isOperator) {
      return this.currentUser?.organisation?.orgLogo
        || this.currentUser?.organisation?.organisationLogo
        || this.companyLogo;
    }

    return this.companyLogo;
  }

  getBrandName(): string {
    if (this.isOperator) {
      return this.currentUser?.organisation?.orgName
        || this.currentUser?.organisation?.organisationName
        || this.currentUser?.organisationName
        || 'Operator';
    }

    return 'Riya Shuma';
  }

  useCompanyLogo(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (image.src.endsWith(this.companyLogo)) {
      return;
    }

    image.src = this.companyLogo;
  }

  getRoleLabel(): string {
    return this.getCurrentRole().replace(/_/g, ' ');
  }

  private getCurrentRole(): UserRole {
    const role = this.currentUser?.userRoleName
      || this.currentUser?.userRole
      || this.currentUser?.userRoleID
      || UserRole.PASSENGER;
    return normalizeUserRole(role);
  }
}
